import { useMemo } from 'react';
import { Platform, useWindowDimensions } from 'react-native';

import { Box, HStack, VStack, theme } from '@squire/ui';

import { Skeleton } from '@/components/_shared/Skeleton';

import { styles } from './styles';
import type {
  ChooseTimeLoadingScreenProps,
  TimeSlotGridSkeletonProps,
} from './types';

const DEFAULT_GRID_TIME_SLOT_COUNT = 12;
const DEFAULT_FULL_SCREEN_TIME_SLOT_COUNT = 21;

/** Reusable time slot grid skeleton */
const TimeSlotGridSkeleton = ({
  count = DEFAULT_GRID_TIME_SLOT_COUNT,
}: TimeSlotGridSkeletonProps) => {
  const { width } = useWindowDimensions();

  const numColumns = useMemo(() => {
    if (Platform.OS === 'web') {
      if (width >= theme.breakpoints.tablet) return 4;
      if (width >= 500) return 3;
    }
    return 3;
  }, [width]);

  return (
    <HStack style={styles.grid}>
      {Array.from({ length: count }).map((_, index) => (
        <Box key={index} style={styles.timeSlotSkeletonContainer(numColumns)}>
          <Skeleton
            height={46}
            width="100%"
            variant="rectangular"
            style={styles.timeSlotSkeleton}
          />
        </Box>
      ))}
    </HStack>
  );
};

TimeSlotGridSkeleton.displayName = 'TimeSlotGridSkeleton';

const ChooseTimeLoadingScreen = ({
  testID,
  timeSlotCount = DEFAULT_FULL_SCREEN_TIME_SLOT_COUNT,
  showHeader = true,
}: ChooseTimeLoadingScreenProps) => {
  return (
    <VStack style={styles.page} testID={testID}>
      {showHeader && (
        <VStack space={theme.spacing[3]}>
          <Skeleton variant="text" width="45%" />
          <Skeleton variant="text" width="35%" />
        </VStack>
      )}

      <TimeSlotGridSkeleton count={timeSlotCount} />
    </VStack>
  );
};

ChooseTimeLoadingScreen.displayName = 'ChooseTimeLoadingScreen';

export { ChooseTimeLoadingScreen, TimeSlotGridSkeleton };
